"use client";

import React, { useRef } from "react";
import { Check, X, ShieldCheck, Zap, UserCheck, TrendingUp, PiggyBank, ArrowRight, Sparkles } from "lucide-react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { useGSAP } from "@gsap/react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Link } from "@/components/common/Link";
gsap.registerPlugin(ScrollTrigger, useGSAP);
const benefitsData = [{
  id: "1",
  title: "Direct Senior Expertise vs. Junior Hand-offs",
  consultantPoint: "You work 100% directly with me—Yassine Essaadi. Every audit, strategy, and technical task is handled by a senior specialist.",
  agencyPoint: "Pitch led by directors, but daily account tasks are offloaded to junior executives managing 15+ clients.",
  badgeText: "Direct Access",
  iconName: "UserCheck"
}, {
  id: "2",
  title: "Maximum Cost Efficiency vs. Agency Overhead",
  consultantPoint: "Every pound of your retainer pays directly for SEO strategy and execution—not bloated agency overhead.",
  agencyPoint: "Up to 50% of fees cover slick office spaces, sales teams, and account management hierarchies.",
  badgeText: "High ROI",
  iconName: "PiggyBank"
}, {
  id: "3",
  title: "Bespoke Strategy vs. Mass Checklists",
  consultantPoint: "Custom SEO roadmaps engineered specifically for your technical stack, industry nuances, and business goals.",
  agencyPoint: "Standardized, assembly-line checklists designed to satisfy billable hours rather than drive actual growth.",
  badgeText: "Tailored Plan",
  iconName: "Zap"
}, {
  id: "4",
  title: "Agile Execution vs. Bureaucratic Delays",
  consultantPoint: "Fast turnaround with direct communication. Critical technical fixes and strategy shifts happen in days.",
  agencyPoint: "Multiple layers of internal sign-offs, account manager bottlenecks, and rigid scope-change friction.",
  badgeText: "Agile Speed",
  iconName: "TrendingUp"
}, {
  id: "5",
  title: "Transparent Metrics vs. Vanity Reports",
  consultantPoint: "Plain-English reporting focused on search visibility, qualified leads, and actual commercial impact.",
  agencyPoint: "Automated monthly PDF dumps filled with vanity metrics that obscure a lack of business revenue growth.",
  badgeText: "Honest Metrics",
  iconName: "ShieldCheck"
}, {
  id: "6",
  title: "Dedicated Focus vs. Diluted Attention",
  consultantPoint: "Strict client cap ensures your project gets deep strategic focus, ongoing proactive optimization, and care.",
  agencyPoint: "Account managers stretched thin across dozens of brands, reacting to fires rather than building momentum.",
  badgeText: "Selective Capacity",
  iconName: "Sparkles"
}];
export default function Consultant() {
  const containerRef = useRef<HTMLDivElement>(null);
  useGSAP(() => {
    const el = containerRef.current;
    if (!el) return;
    const mm = gsap.matchMedia();
    mm.add("(prefers-reduced-motion: no-preference)", () => {
      gsap.set(".reveal-card", {
        y: 35
      });
      gsap.to(".reveal-card", {
        opacity: 1,
        y: 0,
        duration: 0.65,
        stagger: 0.1,
        ease: "power3.out",
        scrollTrigger: {
          trigger: el,
          start: "top 80%",
          once: true
        }
      });
    });
    mm.add("(prefers-reduced-motion: reduce)", () => {
      gsap.set(".reveal-card", {
        opacity: 1,
        y: 0
      });
    });
    const refresh = () => ScrollTrigger.refresh();
    requestAnimationFrame(refresh);
    if (document.readyState === "complete") refresh();else window.addEventListener("load", refresh, {
      once: true
    });
    document.fonts?.ready.then(refresh);
  }, {
    scope: containerRef
  });
  return <section id="consultantvsagencybenefits" data-nav="light" className="relative bg-background text-foreground py-20 md:py-28 overflow-hidden">
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute -top-24 -right-24 w-96 h-96 bg-accent/10 rounded-full blur-3xl" />
        <div className="absolute top-1/2 -left-24 w-80 h-80 bg-primary/5 rounded-full blur-3xl" />
      </div>

      <div ref={containerRef} className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="text-center max-w-3xl mx-auto mb-16 md:mb-20">
          <Badge variant="outline" className="mb-4 px-3 py-1 text-xs border-accent/40 bg-accent/10 text-foreground font-medium rounded-full">
            Personal Consultancy vs Traditional Agency
          </Badge>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-foreground font-default mb-6">
            Why Working Directly With Me Delivers Superior Search Results
          </h2>
          <p className="text-base sm:text-lg text-muted-foreground leading-relaxed font-default">
            When you hire YE Consultancy, you don't get an agency account manager passing work to junior executives. You get direct access to me—a dedicated SEO consultant focused exclusively on strategy, technical execution, and sustainable organic growth.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8 mb-16">
          {benefitsData.map((item, index) => <Card key={item.id} data-index={index} className="reveal-card border border-border shadow-md hover:shadow-lg transition-all duration-300 rounded-xl bg-card flex flex-col justify-between" style={{
          opacity: 0
        }}>
              <CardHeader className="pb-4">
                <div className="flex items-center justify-between gap-2 mb-3">
                  <Badge className="bg-accent/15 hover:bg-accent/20 text-foreground border border-accent/30 rounded-md font-semibold text-xs px-2.5 py-1">
                    {item.badgeText}
                  </Badge>
                  <span className="text-xs font-mono text-muted-foreground font-medium">0{index + 1}</span>
                </div>
                <CardTitle className="text-lg font-bold text-foreground font-default leading-snug">
                  {item.title}
                </CardTitle>
              </CardHeader>

              <CardContent className="space-y-4 pt-0 text-sm flex-1 flex flex-col justify-between">
                <div className="p-3.5 rounded-lg bg-background border border-accent/25 space-y-1.5">
                  <div className="flex items-center gap-2 text-primary font-semibold text-xs uppercase tracking-wider">
                    <Check className="size-4 text-accent shrink-0" />
                    <span>Working With Yassine</span>
                  </div>
                  <p className="text-foreground leading-relaxed font-default font-normal">
                    {item.consultantPoint}
                  </p>
                </div>

                <div className="p-3.5 rounded-lg bg-muted/60 border border-border space-y-1.5">
                  <div className="flex items-center gap-2 text-muted-foreground font-semibold text-xs uppercase tracking-wider">
                    <X className="size-4 text-destructive/70 shrink-0" />
                    <span>Traditional SEO Agency</span>
                  </div>
                  <p className="text-muted-foreground leading-relaxed font-default">
                    {item.agencyPoint}
                  </p>
                </div>
              </CardContent>
            </Card>)}
        </div>

        <div className="reveal-card rounded-2xl bg-card border border-border p-8 md:p-12 shadow-lg relative overflow-hidden" style={{
        opacity: 0
      }}>
          <div className="absolute top-0 right-0 w-64 h-64 bg-accent/10 rounded-full blur-2xl pointer-events-none" />
          
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-8 space-y-4">
              <div className="inline-flex items-center gap-2 text-accent font-semibold text-sm">
                <Sparkles className="size-4" />
                <span>My Personal Commitment</span>
              </div>
              <h3 className="text-2xl sm:text-3xl font-bold text-foreground font-default">
                Ready for transparent, hands-on SEO that actually drives revenue?
              </h3>
              <p className="text-muted-foreground text-base leading-relaxed font-default">
                I limit my client intake so I can immerse myself in your business goals, audit your technical foundation thoroughly, and craft an SEO strategy that delivers measurable growth. Let's discuss your targets.
              </p>
            </div>

            <div className="lg:col-span-4 flex flex-col sm:flex-row lg:flex-col gap-4 justify-center lg:items-end">
              <Button asChild size="lg" className="w-full sm:w-auto font-semibold shadow-md gap-2">
                <Link to="/ContactPage">
                  <span>Book a Free SEO Consultation</span>
                  <ArrowRight className="size-4" />
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="w-full sm:w-auto font-medium">
                <Link to="/CaseStudiesPage">
                  <span>Explore My Case Studies</span>
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>;
}